import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Clapperboard } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    try {
      const result = await login(email, password);
      if (result.success) {
        toast({
          title: 'Login bem-sucedido!',
          description: 'Bem-vindo de volta à sua galeria.',
        });
        navigate('/dashboard', { replace: true });
      } else {
        throw new Error(result.error || 'Erro no login');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      setErrorMessage(error.message || 'Erro no login');
      toast({
        title: 'Erro de Login',
        description: error.message || 'Houve um problema ao tentar fazer login. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | MIDIAAP</title>
        <meta name="description" content="Acesse sua plataforma MIDIAAP." />
      </Helmet>
      <div className="flex items-center justify-center min-h-screen p-4 bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950">
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10">
            <div className="text-center mb-8">
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0], transition: { duration: 1.5, repeat: Infinity, repeatType: 'loop' } }}
                className="inline-block mb-4 p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10"
              >
                <Clapperboard className="h-12 w-12 text-cyan-400" />
              </motion.div>
              <h1 className="text-3xl font-black text-white tracking-tight">MediaHub</h1>
              <p className="text-slate-400 mt-2">Acesse sua galeria de mídia profissional.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-slate-300">Email</label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-slate-800/50 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-slate-300">Senha</label>
                <Input
                  id="password"
                  type="password"
                  placeholder="********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-slate-800/50 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold py-6 rounded-xl shadow-lg shadow-cyan-500/20" disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  'Entrar'
                )}
              </Button>
              {errorMessage && (
                <p className="text-sm text-red-400 text-center">{errorMessage}</p>
              )}
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Ainda não tem conta?{' '}
                <Link
                  to="/register"
                  className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors"
                >
                  Criar conta grátis
                </Link>
              </p>
              <p className="text-slate-600 text-xs mt-2">
                Plano Gratuito com 500 MB de armazenamento.
              </p>
              <p className="mt-3 text-[10px] text-slate-700">
                Ao utilizar o MediaHub, você concorda com os <Link to="/terms" className="hover:text-slate-500">Termos de Serviço e Licença</Link>.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default LoginPage;

